import { FC, useCallback, useMemo } from 'react';
import { Nullable } from 'common-types';
import { useInfinityScroll } from 'react-utils';
import Panel from '../../../elements/Panel/Panel';
import config from '../../common/config/issues-config.json';
import { useIssuesTableHeader } from '../../common/hooks/use-issues-table-header';
import {
  Issue,
  IssuesOrder,
  IssueSortKey,
} from '../../common/types/issues-types';
import BaseIssuesTable from '../../common/components/IssuesTable/BaseIssuesTable';
import {
  handleIssueRowClickResolved,
  loadIssuesResolved,
} from '../actions/resolved-issues-actions';

const ResolvedIssuesTable: FC<React.PropsWithChildren<IssuesTableProps>> = ({
  issues,
  selectedID,
  order,
  orderBy,
  onSort,
  loading,
}) => {
  const columns = useIssuesTableHeader({
    withMenu: false,
    resolvedIssues: true,
  });
  const { onScroll } = useInfinityScroll({
    load: loadIssuesResolved,
    threshold: 0.9,
  });

  const handleClick = useCallback((clickedIssue: Issue) => {
    handleIssueRowClickResolved(clickedIssue.issueId);
  }, []);

  const sortColumnsInfo = useMemo(() => {
    return orderBy && order
      ? [{ columnKey: orderBy, direction: order }]
      : undefined;
  }, [order, orderBy]);

  return (
    <Panel width='100%' height='100%' border='none' display='flex'>
      <BaseIssuesTable
        columns={columns}
        onRowClick={handleClick}
        onScroll={onScroll}
        activeRowKey='issueId'
        activeRowValue={selectedID}
        rowHeight={config.table.rowHeight}
        defaultColumnOptions={{
          sortable: true,
          resizable: false,
        }}
        rows={issues}
        sortColumns={sortColumnsInfo}
        onSort={onSort as (key?: string) => void}
        loading={loading}
      />
    </Panel>
  );
};

export interface IssuesTableProps {
  issues: Issue[];
  selectedID: Nullable<string>;
  order?: Nullable<IssuesOrder>;
  orderBy?: Nullable<string>;
  onSort?: (key: IssueSortKey) => void;
  loading: boolean;
}

export default ResolvedIssuesTable;
